import React from 'react';
import Styled from 'styled';
import { FormattedMessage } from 'react-intl';
import { Formik, Form, Field, FieldProps } from 'formik';

import { AlertContext } from 'lib/alert-context/alert-context';

import SubmitButtonView from 'four-nets/ui/submit-button/submit-button-view';
import TextArea from 'four-nets/ui-v2/form/text-area/text-area';

import Modal from 'four-nets/ui-v2/modal/modal';
import FieldWithLabelView from 'four-nets/ui/form/field-with-label-view';

import defaultStyles from './complain-dialog.scss';

interface Values {
    comment: string;
}

type OnSubmit = (values: Values) => Promise<any>;
type Complain = (options: { onSubmit: OnSubmit; title?: React.ReactNode }) => void;

export interface Props {
    children: (props: { complain: Complain }) => React.ReactNode;
    styles: typeof defaultStyles;
}

interface State {
    open: boolean;
    title: React.ReactNode;
    onSubmit?: OnSubmit;
}

class ComplainDialog extends React.PureComponent<Props, State> {
    static contextType = AlertContext;
    context!: React.ContextType<typeof AlertContext>;

    state: State = {
        open: false,
        title: null,
    };

    close = () => {
        this.setState({
            open: false,
        });
    };

    complain: Complain = ({ title = <FormattedMessage id='COMPLAIN_POST' />, onSubmit }) => {
        this.setState({
            open: true,
            title,
            onSubmit,
        });
    };

    render() {
        const { styles } = this.props;
        const { open, title } = this.state;
        const { retry, confirm, acknowledge } = this.context;

        return (
            <React.Fragment>
                <Formik
                    initialValues={{ comment: '' } as Values}
                    onSubmit={(values, actions) => {
                        const onAccept = () => {
                            actions.setSubmitting(true);
                            this.state.onSubmit!(values)
                                .then(() => {
                                    acknowledge({
                                        body: <FormattedMessage id='COMPLAINT_DID_SEND' />,
                                        onClose: () => {
                                            this.close();
                                            actions.resetForm();
                                        },
                                    });
                                })
                                .catch(error => {
                                    retry({
                                        body: error.message,
                                        onAccept,
                                    });
                                })
                                .finally(() => {
                                    actions.setSubmitting(false);
                                });
                        };

                        onAccept();
                    }}
                >
                    {({ values, isSubmitting, submitForm, setFieldValue }) => (
                        <Modal
                            title={title}
                            open={open}
                            onClose={() => {
                                if (values.comment) {
                                    confirm({
                                        body: <FormattedMessage id='COMPLAINT_DISCARD' />,
                                        acceptButtonLabel: <FormattedMessage id='BUTTON_LABEL_CLOSE' />,
                                        onAccept: this.close,
                                    });
                                } else {
                                    this.close();
                                }
                            }}
                        >
                            <Form className={styles.form}>
                                <FieldWithLabelView
                                    field={
                                        <React.Fragment>
                                            <Field name='comment'>
                                                {({ field }: FieldProps<Values>) => (
                                                    <TextArea
                                                        {...field}
                                                        // FIXME: 'field.onChange' handler doesn't work with TextArea
                                                        onChange={e => setFieldValue('comment', e.target.value)}
                                                    />
                                                )}
                                            </Field>
                                            <span className={styles.info}>
                                                * &nbsp;
                                                <FormattedMessage id='FORM_TEXT_FIELD_NOT_REQUIRED' />
                                            </span>
                                            <SubmitButtonView disabled={isSubmitting} onClick={submitForm}>
                                                <span className={styles.label}>
                                                    <FormattedMessage id='FORUM_BUTTON_LABEL_SUBMIT_COMPLAINT' />
                                                </span>
                                            </SubmitButtonView>
                                        </React.Fragment>
                                    }
                                    label={<FormattedMessage id='FORM_FIELD_LABEL_COMPLAINT_REASON' />}
                                />
                            </Form>
                        </Modal>
                    )}
                </Formik>
                {this.props.children({ complain: this.complain })}
            </React.Fragment>
        );
    }
}

export default Styled(defaultStyles)(ComplainDialog);
